import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { ArrowRight, ArrowLeft, Download, Youtube, Headphones, FileText } from "lucide-react";

import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import StoryCardApi from "@/components/editorial/StoryCardApi";
import CountUp from "@/components/editorial/CountUp";
import ShareButtons from "@/components/editorial/ShareButtons";
import SponsorStrip from "@/components/editorial/SponsorStrip";
import OpeningSponsorBand from "@/components/editorial/OpeningSponsorBand";
import SponsorVideoBadge from "@/components/SponsorVideoBadge";
import DownloadGateModal from "@/components/DownloadGateModal";
import { fetchStoryBySlug } from "@/lib/stories";
import { fetchSponsorsByStory } from "@/lib/sponsors";
import api from "@/lib/api";

export default function StoryDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [related, setRelated] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setStory(null);
    setSponsors([]);
    window.scrollTo(0, 0);
    fetchStoryBySlug(slug)
      .then((data) => {
        setStory(data.story);
        setRelated(data.related || []);
        if (data.story) {
          // Phase 6.2 · GA4 view_story event
          import("@/lib/analytics").then(({ trackEvent }) =>
            trackEvent("view_story", {
              story_slug: data.story.slug,
              story_industry: data.story.industry,
              story_city: data.story.city,
            })
          );
        }
        if (data.story?.id) {
          fetchSponsorsByStory(data.story.id)
            .then((list) => setSponsors(list || []))
            .catch(() => setSponsors([]));
        }
      })
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center text-charcoal/40 pt-28">
          Cargando historia…
        </div>
      </SiteLayout>
    );
  }

  if (notFound) {
    return (
      <SiteLayout>
        <></>
        <main className="min-h-[70vh] flex items-center justify-center bg-cream text-charcoal pt-28 pb-24">
          <Container>
            <div className="max-w-xl mx-auto text-center" data-testid="story-not-found">
              <Eyebrow tone="terracotta" className="mb-4">Historia 404</Eyebrow>
              <h1 className="font-serif text-5xl md:text-6xl font-light mb-5">
                Esta historia<br /><span className="italic">aún no existe.</span>
              </h1>
              <p className="text-charcoal/70 mb-8">
                Puede que la hayamos movido, o que estés buscando una historia que
                todavía estamos filmando.
              </p>
              <Link
                to="/stories"
                className="inline-flex items-center gap-2 text-terracotta hover:text-[#A64826] text-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver a todas las historias
              </Link>
            </div>
          </Container>
        </main>
      </SiteLayout>
    );
  }

  const s = story;
  const canonical = `https://mujeres-motor.com/stories/${s.slug}`;
  const pageTitle = s.seo_title || `${s.name} · ${s.city} · Mujeres Motor`;
  const pageDesc = s.seo_description || s.short_quote;
  const ogImage = s.og_image_url || s.portrait_url;
  const heroBg = s.hero_url || s.portrait_url;
  const publishedDate = s.published_at ? new Date(s.published_at) : null;

  const lightboxSlides = (s.gallery || []).map((g) => ({
    src: g.url,
    description: g.caption,
  }));

  return (
    <SiteLayout transparentHeader>
      <></>

      {/* 0. Opening sponsor band — cinematic title card */}
      <OpeningSponsorBand
        variant="story"
        preferred={sponsors}
        preferredCategory={s.industry}
        entityId={s.id}
      />

      {/* 1. Hero */}
      <section
        data-testid="story-hero"
        className="relative h-[80vh] min-h-[600px] w-full overflow-hidden bg-charcoal"
      >
        <img
          src={heroBg}
          alt={`${s.name} — ${s.industry || ""} — ${s.city || ""}`.replace(/\s—\s+$/, "").trim()}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-charcoal/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-transparent to-charcoal/95" />
        <Container className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-28">
          <div className="max-w-4xl animate-fade-up">
            <p className="text-gold text-[11px] uppercase tracking-[0.3em] mb-5">
              {s.season && <>{s.season} · </>}{s.city} · {s.industry}
            </p>
            <h1
              data-testid="story-title"
              className="font-serif font-light text-cream leading-[0.98] tracking-tight"
              style={{ fontSize: "clamp(2.75rem, 6.5vw, 6rem)" }}
            >
              {s.name}
            </h1>
            {s.long_quote && (
              <p className="font-serif italic text-cream/90 mt-8 max-w-3xl text-xl md:text-2xl lg:text-3xl leading-[1.3]">
                “{s.long_quote}”
              </p>
            )}
            {publishedDate && (
              <p className="text-cream/50 text-xs uppercase tracking-[0.22em] mt-8">
                Publicado · {publishedDate.toLocaleDateString("es-MX", {
                  year: "numeric", month: "long", day: "numeric",
                })}
              </p>
            )}
          </div>
        </Container>
      </section>

      {/* 2. Video documental */}
      {s.video_url && (
        <section data-testid="story-video" className="bg-charcoal text-cream py-20 md:py-28">
          <Container>
            <div className="max-w-5xl mx-auto">
              <Eyebrow tone="gold" className="mb-4 text-center block">
                Mini documental
              </Eyebrow>
              <h2 className="font-serif font-light text-center text-3xl md:text-4xl mb-10">
                {s.name} · <span className="italic">{s.city}</span>
              </h2>
              <div className="relative aspect-video w-full bg-charcoal-soft rounded-sm overflow-hidden">
                {sponsors.find((sp) => sp.tier === "episode") && (
                  <SponsorVideoBadge
                    sponsor={sponsors.find((sp) => sp.tier === "episode")}
                    storageKey={`story-${s.slug}`}
                    tone="light"
                  />
                )}
                <iframe
                  src={s.video_url}
                  title={`Mini documental ${s.name}`}
                  className="absolute inset-0 h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <p className="text-center text-cream/50 text-xs mt-5 uppercase tracking-[0.22em]">
                Producción Baklight · Mujeres Motor {s.season}
              </p>
            </div>
          </Container>
        </section>
      )}

      {/* 3. Bio */}
      {s.bio && (
        <section data-testid="story-bio" className="bg-cream py-20 md:py-28">
          <Container>
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-4">
                <div className="relative aspect-[3/4] overflow-hidden rounded-sm max-w-sm">
                  <img
                    src={s.portrait_url}
                    alt={`Retrato de ${s.name}${s.industry ? ` — ${s.industry}` : ""}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
              <div className="lg:col-span-7 lg:col-start-6">
                <Eyebrow tone="terracotta" className="mb-4">Quién es</Eyebrow>
                <h2 className="font-serif font-light text-3xl md:text-4xl mb-6">
                  {s.name}
                </h2>
                <p className="text-lg leading-relaxed text-charcoal/85 max-w-2xl">
                  {s.bio}
                </p>
              </div>
            </div>
          </Container>
        </section>
      )}

      {/* 4. Narrativa con dropcap */}
      {s.story_body && (
        <section data-testid="story-body" className="bg-cream-dark py-20 md:py-28">
          <Container>
            <article className="mx-auto max-w-[680px] story-prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {s.story_body}
              </ReactMarkdown>
            </article>
          </Container>
        </section>
      )}

      {/* 5. Galería · masonry */}
      {s.gallery && s.gallery.length > 0 && (
        <section data-testid="story-gallery" className="bg-cream py-20 md:py-28">
          <Container>
            <Eyebrow tone="terracotta" className="mb-6">Galería</Eyebrow>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
              {s.gallery.map((g, i) => {
                // masonry ratio variety: i%3 → aspect
                const ratio = i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-[4/3]" : "aspect-square";
                return (
                  <figure
                    key={i}
                    data-testid={`gallery-item-${i}`}
                    onClick={() => setLightboxIndex(i)}
                    className="group cursor-pointer mb-4 break-inside-avoid"
                  >
                    <div className={`relative ${ratio} overflow-hidden rounded-sm`}>
                      <img
                        src={g.url}
                        alt={g.caption || `${s.name} — galería de la historia ${i + 1}`}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[800ms] group-hover:scale-[1.05]"
                      />
                    </div>
                    {g.caption && (
                      <figcaption className="mt-2 text-xs text-charcoal/60">
                        {g.caption}
                      </figcaption>
                    )}
                  </figure>
                );
              })}
            </div>
          </Container>
          <Lightbox
            open={lightboxIndex >= 0}
            close={() => setLightboxIndex(-1)}
            index={lightboxIndex}
            slides={lightboxSlides}
          />
        </section>
      )}

      {/* 6. Métricas de impacto */}
      {s.impact_metrics && s.impact_metrics.length > 0 && (
        <section data-testid="story-metrics" className="bg-charcoal text-cream py-20 md:py-28">
          <Container>
            <Eyebrow tone="gold" className="mb-6 text-center block">
              Impacto
            </Eyebrow>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
              {s.impact_metrics.map((m, i) => (
                <div key={i} data-testid={`metric-${i}`}>
                  <p className="font-serif font-light text-5xl md:text-7xl text-terracotta leading-none">
                    <CountUp value={m.value} />
                    {m.unit && (
                      <span className="text-2xl md:text-3xl text-cream/70 ml-2 font-sans">
                        {m.unit}
                      </span>
                    )}
                  </p>
                  <p className="mt-4 text-xs md:text-sm uppercase tracking-[0.22em] text-cream/70">
                    {m.label}
                  </p>
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* 7. Podcast · prominent */}
      {(s.podcast_embed_url || s.podcast_youtube_url) && (
        <section data-testid="story-podcast" className="bg-cream py-20 md:py-28">
          <Container>
            <div className="max-w-3xl mx-auto">
              <Eyebrow tone="terracotta" className="mb-4">Podcast</Eyebrow>
              <h2 className="font-serif font-light text-3xl md:text-4xl mb-8">
                Escucha la <span className="italic">conversación completa.</span>
              </h2>

              <div className="flex flex-wrap gap-3 mb-8">
                {s.podcast_youtube_url && (
                  <a
                    href={s.podcast_youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="podcast-youtube"
                    className="inline-flex items-center gap-2 rounded-full bg-[#C4302B] text-cream px-6 py-3 text-sm font-medium hover:bg-[#9a2520] transition-all duration-[400ms]"
                  >
                    <Youtube className="h-4 w-4" />
                    Ver en YouTube
                  </a>
                )}
                {s.podcast_embed_url && (
                  <a
                    href={s.podcast_embed_url.replace("/embed/", "/").replace("open.spotify.com", "open.spotify.com")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="podcast-spotify"
                    className="inline-flex items-center gap-2 rounded-full bg-[#1DB954] text-charcoal px-6 py-3 text-sm font-medium hover:bg-[#1ed760] transition-all duration-[400ms]"
                  >
                    <Headphones className="h-4 w-4" />
                    Escuchar en Spotify
                  </a>
                )}
              </div>

              {s.podcast_embed_url && (
                <div className="rounded-sm overflow-hidden">
                  <iframe
                    src={s.podcast_embed_url}
                    className="w-full"
                    height="232"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                    loading="lazy"
                    title={`Podcast con ${s.name}`}
                  />
                </div>
              )}
            </div>
          </Container>
        </section>
      )}

      {/* 7b. PDF download */}
      {s.article_pdf_url && (
        <section data-testid="story-pdf" className="bg-cream-dark py-16 md:py-20 border-t border-charcoal/10">
          <Container>
            <div className="max-w-3xl mx-auto flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <Eyebrow tone="terracotta" className="mb-3">Descargable</Eyebrow>
                <h3 className="font-serif font-light text-2xl md:text-3xl text-charcoal leading-[1.15]">
                  Lleva contigo la <span className="italic">historia completa.</span>
                </h3>
                <p className="text-charcoal/70 mt-3 text-sm md:text-base max-w-lg">
                  Versión imprimible y editorial del artículo, con fotografías en alta resolución.
                  {s.article_pdf_size_kb && <> · {Math.round(s.article_pdf_size_kb / 1024 * 10) / 10} MB</>}
                </p>
              </div>
              <a
                href={`${api.defaults.baseURL}/stories/${s.slug}/download-article`}
                onClick={(e) => { e.preventDefault(); setGateOpen(true); }}
                rel="noopener noreferrer"
                data-testid="download-pdf-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm font-medium hover:bg-[#A64826] transition-all duration-[400ms] whitespace-nowrap cursor-pointer"
              >
                <Download className="h-4 w-4" />
                Descargar PDF
                <FileText className="h-4 w-4 opacity-70" />
              </a>
            </div>
          </Container>
          <DownloadGateModal
            open={gateOpen}
            onClose={() => setGateOpen(false)}
            downloadUrl={`${api.defaults.baseURL}/stories/${s.slug}/download-article`}
            title={`Descarga la historia de ${s.name}`}
            subtitle="PDF editorial con foto y texto. Si quieres, déjanos tu email para recibir nuevas historias cada mes."
          />
        </section>
      )}

      {/* 8. Sponsors · episode + category */}
      {sponsors.length > 0 && (
        <section data-testid="story-sponsors" className="bg-cream-dark py-14 md:py-16 border-t border-charcoal/5">
          <Container>
            <div className="max-w-5xl mx-auto space-y-10">
              {sponsors.filter((sp) => sp.tier === "episode").length > 0 && (
                <SponsorStrip
                  tier="episode"
                  eyebrow="Episodio presentado por"
                  tone="light"
                  size="xl"
                  centered
                  initialSponsors={sponsors.filter((sp) => sp.tier === "episode")}
                  testid="story-episode-strip"
                />
              )}
              {sponsors.filter((sp) => sp.tier === "category").length > 0 && (
                <SponsorStrip
                  tier="category"
                  eyebrow={`Partner de categoría · ${s.industry}`}
                  tone="light"
                  size="lg"
                  centered
                  initialSponsors={sponsors.filter((sp) => sp.tier === "category")}
                  testid="story-category-strip"
                />
              )}
            </div>
          </Container>
        </section>
      )}

      {/* Legacy inline sponsor (story-level override) */}
      {s.sponsor_name && !sponsors.length && (
        <section data-testid="story-sponsor" className="bg-cream-dark py-12">
          <Container>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
              <p className="text-xs uppercase tracking-[0.3em] text-charcoal/60">
                Este episodio es presentado por
              </p>
              {s.sponsor_logo_url && (
                <img
                  src={s.sponsor_logo_url}
                  alt={s.sponsor_name}
                  className="h-8 md:h-10 object-contain"
                />
              )}
              <p className="font-serif text-xl">{s.sponsor_name}</p>
            </div>
          </Container>
        </section>
      )}

      {/* 9. Share + Related */}
      <section className="bg-cream py-20 md:py-28 border-t border-charcoal/10">
        <Container>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-14">
            <Eyebrow tone="terracotta">Comparte esta historia</Eyebrow>
            <ShareButtons
              title={`${s.name} · ${s.short_quote}`}
              url={canonical}
              type="stories"
              slug={s.slug}
              sponsorSlug={sponsors.find((sp) => sp.tier === "episode")?.slug}
            />
          </div>

          {related.length > 0 && (
            <>
              <div className="flex items-end justify-between mb-8">
                <h2 className="font-serif font-light text-3xl md:text-4xl">
                  Otras <span className="italic">historias</span>
                </h2>
                <Link
                  to="/stories"
                  className="hidden md:inline-flex items-center gap-1.5 text-sm text-terracotta hover:text-[#A64826]"
                >
                  Ver todas
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div
                data-testid="related-grid"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
              >
                {related.map((r, i) => (
                  <StoryCardApi key={r.id} story={r} index={i} />
                ))}
              </div>
            </>
          )}
        </Container>
      </section>

      {/* 10. CTA final */}
      <section className="bg-charcoal text-cream py-24 md:py-32">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <Eyebrow tone="gold" className="mb-6 block">Postula</Eyebrow>
            <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">
              ¿Conoces a una mujer que<br />
              <span className="italic">deba estar aquí?</span>
            </h2>
            <Link
              to="/nominate"
              data-testid="story-cta-nominate"
              className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-8 py-4 text-base font-medium hover:bg-[#A64826] transition-all duration-[400ms]"
            >
              Postula una historia
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>

          {/* Production credits */}
          <div className="mt-20 pt-10 border-t border-cream/10 max-w-5xl mx-auto">
            <SponsorStrip
              tier="content"
              eyebrow="Créditos de producción"
              tone="dark"
              size="md"
              centered
              testid="story-content-strip"
            />
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}
